import { useMutation, useQueryClient, useQuery, QueryResult, UseMutationResult } from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type {
    CrossBorderReceiver,
    CrossBorderReceiverRequest,
    CrossBorderReceiverResponse,
    Iban,
    ReceiverId,
} from '../generated-types';
import {
    getCrossBorderReceivers,
    deleteCrossBorderReceiver,
    updateCrossBorderReceiver,
} from '../services/crossBorderReceiverService';

export const useCrossBorderReceivers = (isEnabled = true, options = {}): QueryResult<CrossBorderReceiver[]> =>
    useQuery<CrossBorderReceiver[]>({
        ...options,
        queryKey: ['cross-border-receivers'],
        queryFn: getCrossBorderReceivers,
        enabled: isEnabled,
    });

export const useCrossBorderReceiver = (iban: Iban | undefined, enabled = true) => {
    const { data: receivers, isLoading, isFetched } = useCrossBorderReceivers(enabled && !!iban);
    const data = receivers?.find(
        (receiver) => receiver.account.type === 'IBAN' && receiver.account.number === iban?.value,
    );
    return { data, isLoading, isFetched };
};

export const useDeleteCrossBorderReceiver = (
    deleteSpinner: (value: boolean) => void,
    deleteError: (value: RequestExceptionWithResponse) => void,
): UseMutationResult<CrossBorderReceiver, RequestExceptionWithResponse, ReceiverId> => {
    return useMutation<CrossBorderReceiver, RequestExceptionWithResponse, ReceiverId>({
        mutationFn: deleteCrossBorderReceiver,
        onSuccess: () => deleteSpinner(false),
        onError: (error: RequestExceptionWithResponse) => deleteError(error),
    });
};

interface UpdateReceiver extends CrossBorderReceiverRequest {
    receiverId: ReceiverId;
}

export const useUpdateCrossBorderReceiver = (
    updateSpinner: (value: boolean) => void,
    updateError: (value: RequestExceptionWithResponse) => void,
) =>
    useMutation<CrossBorderReceiverResponse, RequestExceptionWithResponse, UpdateReceiver>({
        mutationFn: ({ receiverId, ...updateRequest }) => updateCrossBorderReceiver(receiverId, updateRequest),
        onSuccess: () => updateSpinner(false),
        onError: (error: RequestExceptionWithResponse) => updateError(error),
    });

export const useRefreshCrossBorderReceivers = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: ['cross-border-receivers'] });
};
